import { components } from "@libertify/api-types/media";
import {
  BrandKitAsset,
  ContactResponse,
  PresenterProfileResponse,
  Slide,
} from "pages/media/types/";
import { MediaTemplateResponse, TemplateItem } from "types/";
import {
  extractTextProperties,
  extractTextStyleProperties,
  generateGetPositionFn,
  getAvatarShapeBackgroundColor,
  mapServerColor,
  getShapeType,
  getTextAlign,
  isLineShape,
  isValidShapeGraphics,
  ProcessedTemplateItems,
  processTemplateAttributes,
  processTemplateSlideItems,
  resolveParameteredTextPathToSlidePlaceholder,
  isGradient,
} from "./itemsHelper";
import { mapTable } from "./mapTable";
import {
  AvatarShape,
  BodyView,
  Figure,
  ITEM_TYPES,
  LINE_ORIENTATIONS,
  MainImage,
  MediaItem,
  MediaKind,
  ParameteredText,
  Shape,
  SLIDE_TYPE_TO_LAYOUT,
  TemplateItemWithLayerIndex,
  TemplateState,
} from "../types";
import { extractMask } from "./maskHelper";
import { rgbaToHex } from "utils/";
import {
  DEFAULT_HEADER_SIZE,
  DEFAULT_MUSIC_VOLUME,
  DEFAULT_HEADER_LAYER_INDEX,
  DEFAULT_PRESENTER_LAYER_INDEX,
  DEFAULT_TITLE_LINE_HEIGHT,
} from "../constants";
import { DEFAULT_TEMPLATE_STATE } from "../store/useTemplateEditorStore";
import { adjustSizeToViewportIfNeeded, mapAlignment } from ".";
import { nanoid } from "nanoid";

type TemplateAssets = {
  brandKitData: {
    logo: BrandKitAsset | undefined;
    audio: BrandKitAsset | undefined;
  };
  initialPresenter: PresenterProfileResponse | undefined;
  contact?: ContactResponse;
  presenterRatio: number | undefined;
  logoRatio: number | undefined;
  forceAdjustFont?: boolean;
};

type SlideWithCommonTemplateProps = components["schemas"]["SlideDefinition"] & {
  id: string; // id of the template containing the slides
  avatarShot: components["schemas"]["AvatarShot"] | null;
  music: components["schemas"]["MusicOverride"] | null;
  speech: components["schemas"]["MediaTemplate"]["speech"];
};

const getSlide = (slides: Slide[], id: string) => {
  return slides.find((slide) => slide.uuid === id);
};

export const mapServerTemplateSlidesToTemplates = (
  data: MediaTemplateResponse[],
  slides: Slide[],
  mediaAspectRatio: number,
  commonAssets: TemplateAssets,
  // When provided, resolves per-chapter assets (presenter + its image ratio)
  // for each slide from its section, so each chapter's avatar is mapped against
  // its own presenter instead of the single project-level one.
  resolveSlideAssets?: (sectionId: string | undefined) => TemplateAssets
) => {
  const templateSlides = data
    .flatMap(({ id, template }) =>
      template.slides?.map((slide) => ({
        ...slide,
        id,
        avatarShot: template.avatar_shot,
        music: template.music ?? null,
        speech: template.speech,
      }))
    )
    .filter((slide): slide is SlideWithCommonTemplateProps => !!slide);

  return new Map(
    templateSlides.map((slide) => {
      const slideAssets = resolveSlideAssets
        ? resolveSlideAssets(getSlide(slides, slide.name)?.section_id)
        : commonAssets;
      return [
        slide.name,
        mapSlideToTemplateState(slide, slides, mediaAspectRatio, slideAssets),
      ];
    })
  );
};

export const mapSlideToTemplateState = (
  slide: SlideWithCommonTemplateProps,
  slides: Slide[],
  mediaAspectRatio: number,
  commonAssets: TemplateAssets,
  disabledNames?: Set<string>
): TemplateState => {
  const { sprite_definitions: items, avatarShot, music, speech } = slide;

  const {
    brandKitData,
    initialPresenter,
    contact,
    presenterRatio,
    logoRatio,
    forceAdjustFont,
  } = commonAssets;

  const { logo: brandKitLogo, audio } = brandKitData;

  const {
    headerRectangle,
    headerBanner,
    bodyRectangle,
    bodyImage,
    bodyVideo,
    avatarShapeItem,
    avatarItem,
    imageItemsArray,
    videoItemsArray,
    logo: logoItem,
    customTextItemsArray,
    shapeItemsArray,
    mainImageItem,
    parameteredTextItemsArray,
    figureNumberItem,
    figureUnitsItem,
    slideLayoutShapeItemsArray,
    tableItem,
  } = processTemplateSlideItems(items);

  const getPosition = generateGetPositionFn(items);

  const slideData = getSlide(slides, slide.name);
  const slideLayoutType = slideData?.slide_type
    ? SLIDE_TYPE_TO_LAYOUT[slideData.slide_type]
    : undefined;

  const background = mapBackground(bodyRectangle, bodyImage, bodyVideo);
  const header = mapHeader(headerRectangle, headerBanner, getPosition);
  const logo = mapLogo(
    logoItem,
    brandKitLogo,
    logoRatio,
    mediaAspectRatio,
    getPosition
  );

  const presenter = mapPresenter(
    avatarItem,
    avatarShapeItem,
    initialPresenter,
    contact,
    avatarShot,
    presenterRatio,
    mediaAspectRatio,
    getPosition
  );
  const images = mapMediaItems(imageItemsArray);
  const videos = mapMediaItems(videoItemsArray);
  const customTexts = mapCustomTexts(customTextItemsArray);
  const shapes = mapShapes(shapeItemsArray, getPosition);
  const parameteredTexts = mapParameteredTexts(
    parameteredTextItemsArray,
    getPosition,
    slideData,
    forceAdjustFont,
    disabledNames
  );

  const mainImage = mapMainImage(mainImageItem, getPosition, disabledNames);
  const slideLayoutShapes = mapShapes(slideLayoutShapeItemsArray, getPosition);
  const figureNumber = figureNumberItem
    ? mapParameteredTexts(
        [figureNumberItem],
        getPosition,
        slideData,
        undefined,
        disabledNames
      )[0]
    : undefined;
  const figure = figureNumber
    ? {
        number: figureNumber,
        units: mapToFigureUnits(
          figureUnitsItem,
          slideData,
          getPosition,
          disabledNames
        ),
      }
    : undefined;

  const table = tableItem ? mapTable(tableItem, getPosition) : undefined;

  const speechDuration =
    speech && "duration" in speech && typeof speech.duration === "number"
      ? speech.duration
      : undefined;

  return {
    ...DEFAULT_TEMPLATE_STATE,
    background,
    transition: {
      ...DEFAULT_TEMPLATE_STATE.transition,
      speechType: speech?.type,
      duration: speechDuration,
    },
    ...(figure ? { figure } : {}),
    ...(header ? { header } : {}),
    ...(logo ? { logo } : {}),
    ...(presenter ? { presenter } : {}),
    images,
    videos,
    customTexts,
    shapes,
    music: music ?? (audio ? { volume: DEFAULT_MUSIC_VOLUME } : undefined),
    parameteredTexts,
    mainImage,
    slideLayoutShapes,
    slideLayoutType,
    table,
  };
};

const mapBackground = (
  bodyRectangle: ProcessedTemplateItems["bodyRectangle"],
  bodyImage: ProcessedTemplateItems["bodyImage"],
  bodyVideo: ProcessedTemplateItems["bodyVideo"]
) => {
  let bodyView: BodyView = "Color";
  if (bodyVideo) {
    bodyView = "Video" as const;
  }
  if (bodyImage && "asset_id" in bodyImage) {
    bodyView = "Picture" as const;
  }

  const bodyRectangleColor = bodyRectangle
    ? mapServerColor(bodyRectangle.color)
    : undefined;

  return {
    bodyView,
    bodyVideo: undefined,
    bodyVideoAsset: bodyVideo?.asset_id,
    bodyImage: undefined,
    bodyImageAsset:
      typeof bodyImage?.asset_id === "string" ? bodyImage.asset_id : undefined,
    bodyColor: bodyRectangleColor,
  };
};

const mapHeader = (
  headerRectangle: TemplateItemWithLayerIndex | null,
  headerBanner: TemplateItemWithLayerIndex | null,
  getPosition: ReturnType<typeof generateGetPositionFn>
) => {
  if (!headerRectangle && !headerBanner) return;

  const {
    position: headerPosition,
    width: headerWidth,
    height: headerHeight,
    reference,
  } = processTemplateAttributes((headerRectangle ?? headerBanner)?.attributes);

  if (!headerWidth || !headerHeight || !headerPosition) {
    console.warn(
      "Header width and height or position is missing",
      headerRectangle,
      headerBanner
    );
    return;
  }

  const position = getPosition(
    headerPosition,
    reference,
    headerWidth,
    headerHeight,
    "Header"
  );

  const headerRectangleColor =
    headerRectangle?.graphics.type === "Rectangle"
      ? mapServerColor(headerRectangle.graphics.color)
      : undefined;

  return {
    view: headerBanner ? "Picture" : "Color",
    position,
    size:
      headerWidth && headerHeight
        ? {
            width: headerWidth,
            height: headerHeight,
          }
        : DEFAULT_HEADER_SIZE,
    background: headerRectangleColor,
    banner: headerBanner
      ? {
          assetId:
            headerBanner.graphics.type === "AssetImage" &&
            typeof headerBanner.graphics.asset_id === "string"
              ? headerBanner.graphics.asset_id
              : undefined,
        }
      : undefined,
    layerIndex:
      (headerRectangle ?? headerBanner)?.layerIndex ??
      DEFAULT_HEADER_LAYER_INDEX,
  };
};

const mapLogo = (
  logoItem: TemplateItemWithLayerIndex | null,
  brandKitLogo: components["schemas"]["BrandKitAsset"] | undefined,
  logoRatio: number | undefined,
  mediaAspectRatio: number,
  getPosition: ReturnType<typeof generateGetPositionFn>
) => {
  if (!logoItem || !brandKitLogo || !logoRatio) return;

  const { position, width, height, reference, rotation } =
    processTemplateAttributes(logoItem.attributes);

  if ((!width && !height) || !position) {
    console.error("Logo width and height or position is missing");
    return;
  }

  let size = {
    width: width || (height! / mediaAspectRatio) * logoRatio,
    height: height || (width! * mediaAspectRatio) / logoRatio,
  };
  if ((size.width * mediaAspectRatio) / size.height !== logoRatio) {
    size.width = (size.height / mediaAspectRatio) * logoRatio;
  }
  size = adjustSizeToViewportIfNeeded(size, mediaAspectRatio);
  const logoPosition = getPosition(
    position,
    reference,
    size.width,
    size.height,
    ITEM_TYPES.Logo
  );

  return {
    showLogo: true,
    assetId: brandKitLogo.asset_id,
    path: brandKitLogo.path,
    position: logoPosition,
    size,
    rotation: rotation ?? undefined,
    layerIndex: logoItem.layerIndex,
  };
};

const mapPresenter = (
  avatarItem: TemplateItemWithLayerIndex | null,
  avatarShapeItem: TemplateItem | null,
  initialPresenter: PresenterProfileResponse | undefined,
  contact: ContactResponse | undefined,
  avatarShot: components["schemas"]["AvatarShot"] | null,
  presenterRatio: number | undefined,
  mediaAspectRatio: number,
  getPosition: ReturnType<typeof generateGetPositionFn>
) => {
  const avatar = avatarShapeItem
    ? avatarShapeItem.attributes
    : avatarItem?.attributes;

  if (!avatar || !initialPresenter?.face_profile || !presenterRatio) return;

  const { position, height, width, animations, reference, rotation } =
    processTemplateAttributes(avatar);

  if ((!width && !height) || !position) {
    console.error("Presenter width and height or position is missing");
    return;
  }

  const size = adjustSizeToViewportIfNeeded(
    {
      width: width || (height! / mediaAspectRatio) * presenterRatio,
      height: height || (width! * mediaAspectRatio) / presenterRatio,
    },
    mediaAspectRatio
  );

  const avatarShapeGraphics = avatarShapeItem?.graphics;
  const avatarShapeBackgroundColor =
    getAvatarShapeBackgroundColor(avatarShapeGraphics);

  let avatarShape: AvatarShape = AvatarShape.Regular;
  if (avatarShapeItem?.graphics.type === "Circle") {
    avatarShape = AvatarShape.Circle;
  } else if (
    avatarShapeItem?.graphics.type === "Rectangle" &&
    avatarShapeBackgroundColor
  ) {
    avatarShape = AvatarShape.Box;
  }

  if (avatarShape !== AvatarShape.Regular) {
    size.width = (size.height / mediaAspectRatio) * presenterRatio;
  }

  const presenterPosition = getPosition(
    position,
    reference,
    size.width,
    size.height,
    ITEM_TYPES.Presenter
  );

  const avatarStroke =
    avatarShapeGraphics &&
    "border" in avatarShapeGraphics &&
    avatarShapeGraphics.border
      ? {
          width: avatarShapeGraphics.border.thickness,
          color: rgbaToHex(avatarShapeGraphics.border.color),
          radius: avatarShapeGraphics.border.radius,
        }
      : undefined;

  return {
    presenter: initialPresenter,
    contactId: contact?.id,
    position: presenterPosition,
    size,
    animations,
    rotation: rotation ?? undefined,
    avatarShape,
    avatarShot: avatarShot ?? "Medium",
    background: avatarShapeBackgroundColor,
    stroke: avatarStroke,
    layerIndex: avatarItem?.layerIndex ?? DEFAULT_PRESENTER_LAYER_INDEX,
  };
};

const mapMediaItems = (items: TemplateItemWithLayerIndex[]): MediaItem[] =>
  items.flatMap((item) => {
    const itemGraphics = item.graphics;
    const itemGraphicsId =
      itemGraphics.type === "AssetImage" ||
      itemGraphics.type === "CustomAssetVideo"
        ? itemGraphics.asset_id
        : undefined;

    if (!itemGraphicsId || typeof itemGraphicsId !== "string") return [];

    const { width, height, animations, position, rotation } =
      processTemplateAttributes(item.attributes);

    if (!width || !height || !position) return [];

    const stroke =
      "border" in itemGraphics && itemGraphics.border
        ? {
            width: itemGraphics.border.thickness,
            color: rgbaToHex(itemGraphics.border.color),
            radius: itemGraphics.border.radius,
          }
        : undefined;

    const x =
      typeof position[0] === "string"
        ? mapAlignment(position[0], width, itemGraphics.type)
        : position[0];
    const y =
      typeof position[1] === "string"
        ? mapAlignment(position[1], height, itemGraphics.type)
        : position[1];

    const mask = extractMask(itemGraphics);

    return [
      {
        id: item.name ?? nanoid(),
        path: undefined,
        assetId: itemGraphicsId,
        position: { x, y },
        size: { width, height },
        animations: animations,
        rotation: rotation ?? undefined,
        stroke: stroke,
        layerIndex: item.layerIndex,
        ...(mask ? { mask } : {}),
      },
    ];
  });

const mapCustomTexts = (customTextItemsArray: TemplateItemWithLayerIndex[]) => {
  return customTextItemsArray.flatMap((customText) => {
    const textAlign = getTextAlign(customText.graphics);
    const { position, width, height, animations, rotation } =
      processTemplateAttributes(customText.attributes);
    const {
      fontAssetId: customTextFontAssetId,
      fontSize: customTextFontSize,
      color: customTextColor,
      textBackground: customTextTextBackground,
      content: customTextContent,
      strokeColor: customTextStrokeColor,
    } = extractTextProperties(customText.graphics) ?? {};

    const { wrapText, adjustFont, upperCase, verticalAlign } =
      extractTextStyleProperties(customText.graphics);

    if (
      !width ||
      !height ||
      !position ||
      !customTextContent ||
      !customTextFontSize ||
      !customTextFontAssetId ||
      !customTextColor
    )
      return [];

    const x =
      typeof position[0] === "string"
        ? mapAlignment(position[0], width, customText.name ?? "Custom Text")
        : position[0];
    const y =
      typeof position[1] === "string"
        ? mapAlignment(position[1], height, customText.name ?? "Custom Text")
        : position[1];

    const textItem = {
      id: customText.name ?? nanoid(),
      content: customTextContent,
      fontSize: customTextFontSize,
      fontAssetId: customTextFontAssetId,
      color: customTextColor,
      strokeColor: customTextStrokeColor,
      backgroundColor: customTextTextBackground,
      position: { x, y },
      size: { width, height },
      animations,
      rotation: rotation ?? undefined,
      textAlign,
      lineHeight: DEFAULT_TITLE_LINE_HEIGHT,
      layerIndex: customText.layerIndex,
      wrapText,
      adjustFont,
      upperCase,
      verticalAlign,
    };

    // Keep the stored (max) font size as-is; the auto-fit is applied for
    // display at render time with the real font (see useFittedFontSize).
    return [textItem];
  });
};

const mapShapes = (
  shapeItemsArray: TemplateItemWithLayerIndex[],
  getPosition: ReturnType<typeof generateGetPositionFn>
) => {
  return shapeItemsArray.flatMap((shape): Shape[] => {
    if (!isValidShapeGraphics(shape.graphics)) return [];
    const { position, width, height, animations, reference, rotation } =
      processTemplateAttributes(shape.attributes);
    if (!position || !shape.name) return [];

    if (isLineShape(shape.graphics)) {
      if (!width && !height) return [];
      const size = {
        width:
          shape.graphics.orientation === LINE_ORIENTATIONS.HORIZONTAL
            ? (width ?? 0)
            : shape.graphics.thickness,
        height:
          shape.graphics.orientation === LINE_ORIENTATIONS.HORIZONTAL
            ? shape.graphics.thickness
            : (height ?? 0),
      };

      const rawPosition = getPosition(
        position,
        reference,
        size.width,
        size.height,
        shape.name ?? ITEM_TYPES.Shape
      );

      // Adjust position from center (server) to top-left (preview)
      const adjustedPosition = {
        x:
          shape.graphics.orientation === LINE_ORIENTATIONS.VERTICAL
            ? rawPosition.x - shape.graphics.thickness / 2
            : rawPosition.x,
        y:
          shape.graphics.orientation === LINE_ORIENTATIONS.HORIZONTAL
            ? rawPosition.y - shape.graphics.thickness / 2
            : rawPosition.y,
      };

      return [
        {
          id: shape.name,
          shapeType: getShapeType(shape.graphics.type),
          size,
          position: adjustedPosition,
          background: mapServerColor(shape.graphics.color),
          animations,
          rotation: rotation ?? undefined,
          layerIndex: shape.layerIndex,
          stroke: undefined,
          orientation: shape.graphics.orientation,
        },
      ];
    }

    if (!width || !height) return [];

    const shapePosition = getPosition(
      position,
      reference,
      width,
      height,
      shape.name ?? ITEM_TYPES.Shape
    );

    return [
      {
        id: shape.name,
        shapeType: getShapeType(shape.graphics.type),
        size: { width, height },
        position: shapePosition,
        background: mapServerColor(shape.graphics.color),
        animations,
        rotation: rotation ?? undefined,
        layerIndex: shape.layerIndex,
        stroke: shape.graphics.border
          ? {
              width: shape.graphics.border.thickness,
              color: rgbaToHex(shape.graphics.border.color),
              radius: shape.graphics.border.radius,
            }
          : undefined,
        orientation: undefined,
      },
    ];
  });
};

export const mapParameteredTexts = (
  parameteredTextItemsArray: TemplateItemWithLayerIndex[],
  getPosition: ReturnType<typeof generateGetPositionFn>,
  slide: Slide | undefined,
  forceAdjustFont?: boolean,
  disabledNames?: Set<string>
): ParameteredText[] => {
  return parameteredTextItemsArray.flatMap((parameteredText) => {
    const { position, width, height, animations, reference, rotation } =
      processTemplateAttributes(parameteredText.attributes);
    const {
      fontAssetId,
      fontSize,
      color,
      textBackground: backgroundColor,
      strokeColor,
      lineHeight,
      textAlign,
      path,
      index,
    } = extractTextProperties(parameteredText.graphics) ?? {};

    const { wrapText, adjustFont, upperCase, verticalAlign } =
      extractTextStyleProperties(parameteredText.graphics);

    if (!width || !position || !fontSize || !fontAssetId || !color || !path)
      return [];

    const parameteredTextPosition = getPosition(
      position,
      reference,
      width,
      height,
      parameteredText.name ?? "parameteredText"
    );

    let textContent: string | null | undefined;
    if (slide) {
      textContent = resolveParameteredTextPathToSlidePlaceholder(
        path,
        slide,
        index
      );
    }
    if (!textContent) {
      console.warn(
        "Parametered text content could not be resolved",
        parameteredText
      );
      return [];
    }

    const itemName = parameteredText.name ?? nanoid();
    const textItem = {
      id: itemName,
      fontSize,
      fontAssetId,
      color,
      content: textContent,
      backgroundColor,
      strokeColor,
      position: parameteredTextPosition,
      size: { width, height },
      animations,
      rotation: rotation ?? undefined,
      textAlign: textAlign ?? "center",
      lineHeight: lineHeight ?? DEFAULT_TITLE_LINE_HEIGHT,
      layerIndex: parameteredText.layerIndex,
      path,
      index,
      animated: parameteredText.graphics.type === "StreamedText",
      wrapText,
      adjustFont: forceAdjustFont ? true : adjustFont,
      upperCase,
      verticalAlign,
      visible: !disabledNames?.has(itemName),
    };

    // Keep the stored (max) font size; auto-fit is applied for display at
    // render time with the real font (see useFittedFontSize).
    return [textItem];
  });
};

const isStringAssetId = (
  assetId: components["schemas"]["AssetImageAttributes"]["asset_id"]
): assetId is string => {
  return typeof assetId === "string";
};

export const mapMainImage = (
  mainImageItem: TemplateItemWithLayerIndex | null,
  getPosition: ReturnType<typeof generateGetPositionFn>,
  disabledNames?: Set<string>
): MainImage | undefined => {
  if (
    !mainImageItem ||
    (mainImageItem.graphics.type !== "AssetImage" &&
      mainImageItem.graphics.type !== "CustomAssetVideo")
  )
    return;

  // A literal asset_id string means the user replaced the slide's main media
  // (mediaType is derived from the graphics type). When asset_id is a
  // parameter-ref object, fall back to the "default" sentinel so save-side
  // preserves the original placeholder reference.
  const assetId = isStringAssetId(mainImageItem.graphics.asset_id)
    ? mainImageItem.graphics.asset_id
    : "default";
  const mediaType: MediaKind | undefined = isStringAssetId(
    mainImageItem.graphics.asset_id
  )
    ? mainImageItem.graphics.type === "CustomAssetVideo"
      ? "video"
      : "image"
    : undefined;

  const { width, height, animations, position, reference, rotation } =
    processTemplateAttributes(mainImageItem.attributes);
  if (!width || !height || !position) return;

  const strokeColor =
    "border" in mainImageItem.graphics && mainImageItem.graphics.border?.color
      ? mapServerColor(mainImageItem.graphics.border.color)
      : undefined;

  const stroke =
    "border" in mainImageItem.graphics && mainImageItem.graphics.border
      ? {
          width: mainImageItem.graphics.border.thickness,
          color: isGradient(strokeColor) ? undefined : strokeColor,
          radius: mainImageItem.graphics.border.radius,
        }
      : undefined;

  const mainImagePosition = getPosition(
    position,
    reference,
    width,
    height,
    mainImageItem.name ?? "Main image"
  );

  const itemName = mainImageItem.name ?? nanoid();
  return {
    id: itemName,
    path: undefined,
    assetId,
    mediaType,
    position: mainImagePosition,
    size: { width, height },
    animations: animations,
    rotation: rotation ?? undefined,
    stroke: stroke,
    layerIndex: mainImageItem.layerIndex,
    visible: !disabledNames?.has(itemName),
  };
};

const FIGURE_UNIT_DEFAULT_HEIGHT_RATE = 0.2;

const ensureFigureUnitHeight = (
  attributes: TemplateItem["attributes"]
): NonNullable<TemplateItem["attributes"]> => {
  if (attributes?.some((attribute) => attribute.type === "Height")) {
    return attributes;
  }
  return [
    ...(attributes ?? []),
    { type: "Height", rate: FIGURE_UNIT_DEFAULT_HEIGHT_RATE },
  ];
};

export const mapToFigureUnits = (
  figureUnitsItem: TemplateItemWithLayerIndex | null,
  slideData: Slide | undefined,
  getPosition: ReturnType<typeof generateGetPositionFn>,
  disabledNames?: Set<string>
): Figure["units"] | undefined => {
  if (!slideData || slideData.slide_type !== "figure") return;
  if (!figureUnitsItem || figureUnitsItem.graphics.type !== "Switch") return;
  const customRaw =
    figureUnitsItem.graphics.options[
      slideData.placeholders.figure.unit ?? ""
    ]?.[0];
  const defaultRaw = figureUnitsItem.graphics.default?.[0];

  const customItem = customRaw
    ? {
        ...(customRaw as TemplateItem),
        attributes: ensureFigureUnitHeight(
          (customRaw as TemplateItem).attributes
        ),
        name: figureUnitsItem.name,
        layerIndex: figureUnitsItem.layerIndex,
      }
    : undefined;
  const defaultItem = defaultRaw
    ? {
        ...(defaultRaw as TemplateItem),
        attributes: ensureFigureUnitHeight(
          (defaultRaw as TemplateItem).attributes
        ),
        name: figureUnitsItem.name,
        layerIndex: figureUnitsItem.layerIndex,
      }
    : undefined;

  const [customText, defaultText] = mapParameteredTexts(
    [customItem, defaultItem].filter(Boolean) as TemplateItemWithLayerIndex[],
    getPosition,
    slideData,
    undefined,
    disabledNames
  );

  return customText ?? defaultText;
};
